import React, { forwardRef } from 'react';
import { 
  Box, Typography, Table, TableBody, TableCell, TableHead, 
  TableRow, Paper, Grid, Divider
} from '@mui/material';
import { formatDateForReport, formatCurrencyForReport } from '../../utils/pdfUtils';

const TransactionReport = forwardRef(({ member, transactions, debts, payments, tenant, reportType = 'all' }, ref) => {
  // Calculate totals
  const totalPayments = payments?.reduce((sum, payment) => sum + (payment.amount || 0), 0) || 0;
  const totalDebts = debts?.filter(debt => debt.status === 'active')
    .reduce((sum, debt) => sum + (debt.amount || 0), 0) || 0;
  const totalTransactions = transactions?.reduce((sum, transaction) => sum + (transaction.amount || 0), 0) || 0;

  const reportDate = new Date().toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });

  return (
    <Box ref={ref} sx={{ 
      p: 4, 
      width: '210mm',
      minHeight: '297mm',
      backgroundColor: 'white',
      color: 'black',
      fontFamily: 'Arial, sans-serif',
      fontSize: '10pt'
    }}>
      {/* Report Header */}
      <Box sx={{ mb: 3, textAlign: 'center' }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 1, color: '#3f51b5' }}>
          Financial Transaction Report
        </Typography>
        <Typography variant="subtitle1" sx={{ mb: 0.5 }}>
          {tenant?.name || 'Organization'} Membership System
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          Generated on {reportDate}
        </Typography>
      </Box>

      <Divider sx={{ mb: 3 }} />

      {/* Member Information */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold', color: '#3f51b5', borderBottom: '1px solid #e0e0e0', pb: 1 }}>
          Member Information
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <Typography variant="body1" sx={{ mb: 0.5 }}>
              <strong>Name:</strong> {member?.userId?.firstName} {member?.userId?.lastName}
            </Typography>
            <Typography variant="body1" sx={{ mb: 0.5 }}>
              <strong>Email:</strong> {member?.userId?.email}
            </Typography>
            <Typography variant="body1" sx={{ mb: 0.5 }}>
              <strong>Member ID:</strong> {member?.membershipNumber}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="body1" sx={{ mb: 0.5 }}>
              <strong>Status:</strong> {member?.status?.toUpperCase()}
            </Typography>
            <Typography variant="body1" sx={{ mb: 0.5 }}>
              <strong>Member Since:</strong> {formatDateForReport(member?.joinDate)}
            </Typography>
            <Typography variant="body1" sx={{ mb: 0.5 }}>
              <strong>Membership Type:</strong> {member?.membershipType?.replace(/^\w/, c => c.toUpperCase())}
            </Typography>
          </Grid>
        </Grid>
      </Box>

      {/* Financial Summary */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold', color: '#3f51b5', borderBottom: '1px solid #e0e0e0', pb: 1 }}>
          Financial Summary
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={4}>
            <Paper sx={{ p: 2, textAlign: 'center', bgcolor: '#e8eaf6' }}>
              <Typography variant="body2" sx={{ mb: 1, color: 'text.secondary' }}>
                Total Payments
              </Typography>
              <Typography variant="h6" sx={{ color: '#009688', fontWeight: 'bold' }}>
                {formatCurrencyForReport(totalPayments)}
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={4}>
            <Paper sx={{ p: 2, textAlign: 'center', bgcolor: '#ffebee' }}>
              <Typography variant="body2" sx={{ mb: 1, color: 'text.secondary' }}>
                Outstanding Debts
              </Typography>
              <Typography variant="h6" sx={{ color: '#f44336', fontWeight: 'bold' }}>
                {formatCurrencyForReport(totalDebts)}
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={4}>
            <Paper sx={{ p: 2, textAlign: 'center', bgcolor: '#e3f2fd' }}>
              <Typography variant="body2" sx={{ mb: 1, color: 'text.secondary' }}>
                Balance
              </Typography>
              <Typography variant="h6" sx={{ color: totalPayments - totalDebts >= 0 ? '#009688' : '#f44336', fontWeight: 'bold' }}>
                {formatCurrencyForReport(totalPayments - totalDebts)}
              </Typography>
            </Paper>
          </Grid>
        </Grid>
      </Box>

      {/* Payment History */}
      {(reportType === 'all' || reportType === 'payments') && payments && payments.length > 0 && (
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold', color: '#009688', borderBottom: '1px solid #e0e0e0', pb: 1 }}>
            Payment History
          </Typography>
          <Table sx={{ mb: 2 }}>
            <TableHead sx={{ bgcolor: '#e0f2f1' }}>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}>Date</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Amount</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Method</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Description</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {payments.map((payment, index) => (
                <TableRow key={index} sx={{ '&:nth-of-type(odd)': { bgcolor: '#f5f5f5' } }}>
                  <TableCell>{formatDateForReport(payment.date)}</TableCell>
                  <TableCell sx={{ color: '#009688', fontWeight: 'medium' }}>
                    {formatCurrencyForReport(payment.amount)}
                  </TableCell>
                  <TableCell>
                    {payment.method?.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                  </TableCell>
                  <TableCell>{payment.description || '-'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <Typography variant="body2" sx={{ textAlign: 'right', fontWeight: 'bold' }}>
            Total Payments: {formatCurrencyForReport(totalPayments)}
          </Typography>
        </Box>
      )}

      {/* Outstanding Debts */}
      {(reportType === 'all' || reportType === 'debts') && debts && debts.length > 0 && (
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold', color: '#f44336', borderBottom: '1px solid #e0e0e0', pb: 1 }}>
            Outstanding Debts
          </Typography>
          <Table sx={{ mb: 2 }}>
            <TableHead sx={{ bgcolor: '#ffebee' }}>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}>Category</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Amount</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Due Date</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Description</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {debts.map((debt, index) => (
                <TableRow key={index} sx={{ 
                  '&:nth-of-type(odd)': { bgcolor: '#f5f5f5' },
                  ...(debt.status !== 'active' && { opacity: 0.7 })
                }}>
                  <TableCell sx={{ textTransform: 'capitalize' }}>
                    {debt.category?.replace(/_/g, ' ')}
                  </TableCell>
                  <TableCell sx={{ 
                    color: debt.status === 'active' ? '#f44336' : 'text.secondary',
                    fontWeight: debt.status === 'active' ? 'medium' : 'normal'
                  }}>
                    {formatCurrencyForReport(debt.amount)}
                  </TableCell>
                  <TableCell>{formatDateForReport(debt.dueDate)}</TableCell>
                  <TableCell sx={{ 
                    textTransform: 'uppercase',
                    color: debt.status === 'active' ? '#f44336' : '#4caf50',
                    fontWeight: 'medium',
                    fontSize: '0.8rem'
                  }}>
                    {debt.status}
                  </TableCell>
                  <TableCell>{debt.description || '-'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <Typography variant="body2" sx={{ textAlign: 'right', fontWeight: 'bold' }}>
            Total Active Debts: {formatCurrencyForReport(totalDebts)}
          </Typography>
        </Box>
      )}

      {/* Money Sent to Member */}
      {(reportType === 'all' || reportType === 'transactions') && transactions && transactions.length > 0 && (
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold', color: '#3f51b5', borderBottom: '1px solid #e0e0e0', pb: 1 }}>
            Money Sent to Member
          </Typography>
          <Table sx={{ mb: 2 }}>
            <TableHead sx={{ bgcolor: '#e8eaf6' }}>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}>Date</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Amount</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Reason</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Description</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {transactions.map((transaction, index) => (
                <TableRow key={index} sx={{ '&:nth-of-type(odd)': { bgcolor: '#f5f5f5' } }}>
                  <TableCell>{formatDateForReport(transaction.date)}</TableCell>
                  <TableCell sx={{ color: '#3f51b5', fontWeight: 'medium' }}>
                    {formatCurrencyForReport(transaction.amount)}
                  </TableCell>
                  <TableCell sx={{ textTransform: 'capitalize' }}>
                    {transaction.reason?.replace(/_/g, ' ') || '-'}
                  </TableCell>
                  <TableCell>{transaction.description || '-'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <Typography variant="body2" sx={{ textAlign: 'right', fontWeight: 'bold' }}>
            Total Money Sent: {formatCurrencyForReport(totalTransactions)}
          </Typography>
        </Box>
      )}

      {/* Footer */}
      <Box sx={{ mt: 4, pt: 2, borderTop: '1px solid #e0e0e0', textAlign: 'center' }}>
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          This is an official financial statement generated from {tenant?.name || 'the organization'}'s membership system.
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary', mt: 1 }}>
          For questions regarding this report, please contact {tenant?.supportEmail || 'the administration'}.
        </Typography>
        <Typography variant="caption" sx={{ display: 'block', mt: 2, color: 'text.disabled' }}>
          Report ID: {Math.random().toString(36).substr(2, 9).toUpperCase()} | Page 1
        </Typography>
      </Box>
    </Box>
  );
});

TransactionReport.displayName = 'TransactionReport';

export default TransactionReport;
